import { Suspense, useMemo, memo, useEffect, useRef, useState } from 'react';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls, Grid, PerspectiveCamera, Environment } from '@react-three/drei';
import { usePCAStore } from '../stores/pcaStore';
import { GuestPoint } from './GuestPoint';
import * as THREE from 'three';

// Camera focus controller - handles single selection and comparison views
function CameraFocusController({ controlsRef }: { controlsRef: React.MutableRefObject<any> }) {
  const { 
    selectedGuest, 
    comparisonGuest, 
    comparisonFirstGuest,
    getGuestByName, 
    axisSelection, 
    cameraPosition, 
    cameraTarget: storeCameraTarget,
    userCoordinates
  } = usePCAStore();
  const { camera } = useThree();
  const isAnimating = useRef(false);
  const animationFrameRef = useRef<number | null>(null);
  const resetRequestedRef = useRef(false);
  
  // Determine which guests to focus on
  const pm1Name = comparisonFirstGuest || selectedGuest;
  const pm2Name = comparisonGuest;
  const isComparisonActive = pm1Name && pm2Name;
  
  // Reset camera when cameraPosition or cameraTarget changes (from resetView)
  useEffect(() => {
    if (controlsRef.current) {
      const isDefaultPosition = 
        cameraPosition[0] === 5 && cameraPosition[1] === 5 && cameraPosition[2] === 5 &&
        storeCameraTarget[0] === 0 && storeCameraTarget[1] === 0 && storeCameraTarget[2] === 0;
      
      if (isDefaultPosition) {
        resetRequestedRef.current = true;
        camera.position.set(...cameraPosition);
        if (controlsRef.current.target) {
          controlsRef.current.target.set(...storeCameraTarget);
          controlsRef.current.update();
        }
        requestAnimationFrame(() => {
          if (controlsRef.current) {
            camera.position.set(...cameraPosition);
            if (controlsRef.current.target) {
              controlsRef.current.target.set(...storeCameraTarget);
              controlsRef.current.update();
            }
            resetRequestedRef.current = false;
          }
        });
      }
    }
  }, [cameraPosition, storeCameraTarget, camera, controlsRef]);

  // Handle camera focus for both single selection and comparison
  useEffect(() => {
    // Cancel any ongoing animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    // Need at least one guest to focus on
    if (!pm1Name) return;

    // Get first position - handle user or guest
    let x1: number, y1: number, z1: number;
    
    if (pm1Name === '__USER__' && userCoordinates) {
      // User point - get coordinates from userCoordinates
      x1 = userCoordinates[axisSelection.x - 1] || 0;
      y1 = userCoordinates[axisSelection.y - 1] || 0;
      z1 = userCoordinates[axisSelection.z - 1] || 0;
    } else {
      // Guest point - get coordinates from guest data
      const guest1 = getGuestByName(pm1Name);
      if (!guest1) return;
      x1 = guest1.coordinates[axisSelection.x - 1] || 0;
      y1 = guest1.coordinates[axisSelection.y - 1] || 0;
      z1 = guest1.coordinates[axisSelection.z - 1] || 0;
    }
    
    const pos1 = new THREE.Vector3(x1, y1, z1);

    let targetPosition: THREE.Vector3;
    let cameraDistance: number;

    if (isComparisonActive && pm2Name) {
      // COMPARISON MODE: Center between both guests
      let x2: number, y2: number, z2: number;
      
      if (pm2Name === '__USER__' && userCoordinates) {
        // User point - get coordinates from userCoordinates
        x2 = userCoordinates[axisSelection.x - 1] || 0;
        y2 = userCoordinates[axisSelection.y - 1] || 0;
        z2 = userCoordinates[axisSelection.z - 1] || 0;
      } else {
        // Guest point - get coordinates from guest data
        const guest2 = getGuestByName(pm2Name);
        if (!guest2) return;
        x2 = guest2.coordinates[axisSelection.x - 1] || 0;
        y2 = guest2.coordinates[axisSelection.y - 1] || 0;
        z2 = guest2.coordinates[axisSelection.z - 1] || 0;
      }
      const pos2 = new THREE.Vector3(x2, y2, z2);

      // Calculate midpoint
      targetPosition = new THREE.Vector3().addVectors(pos1, pos2).multiplyScalar(0.5);

      // Calculate distance between points to determine zoom
      const distanceBetween = pos1.distanceTo(pos2);
      
      // Camera distance should be enough to see both points with padding
      // Use distance * 1.5 as base, with a minimum of 5 units
      cameraDistance = Math.max(5, distanceBetween * 1.5 + 2);
    } else {
      // SINGLE SELECTION: Focus on the selected guest
      targetPosition = pos1;
      cameraDistance = 4.5; // Default distance for single selection
    }

    // Calculate camera position: offset from target (look at from slightly above and to the side)
    const offset = new THREE.Vector3(1, 1, 1).normalize().multiplyScalar(cameraDistance);
    const newCameraPosition = targetPosition.clone().add(offset);

    // Animate camera
    isAnimating.current = true;
    const startPosition = camera.position.clone();
    const startTarget = controlsRef.current?.target?.clone() || new THREE.Vector3(0, 0, 0);
    
    const duration = 800; // Slightly faster for smoother feel
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function (ease-out cubic)
      const eased = 1 - Math.pow(1 - progress, 3);
      
      camera.position.lerpVectors(startPosition, newCameraPosition, eased);
      if (controlsRef.current?.target) {
        controlsRef.current.target.lerpVectors(startTarget, targetPosition, eased);
        controlsRef.current.update();
      }
      
      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        isAnimating.current = false;
        animationFrameRef.current = null;
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [pm1Name, pm2Name, isComparisonActive, axisSelection, camera, getGuestByName, controlsRef, userCoordinates]);

  return null;
}

// Connection line between two guests
function ConnectionLine({ 
  guest1, 
  guest2, 
  axisSelection 
}: { 
  guest1: { coordinates: number[] } | null; 
  guest2: { coordinates: number[] } | null;
  axisSelection: { x: number; y: number; z: number };
}) {
  if (!guest1 || !guest2) return null;

  const x1 = guest1.coordinates[axisSelection.x - 1] || 0;
  const y1 = guest1.coordinates[axisSelection.y - 1] || 0;
  const z1 = guest1.coordinates[axisSelection.z - 1] || 0;

  const x2 = guest2.coordinates[axisSelection.x - 1] || 0;
  const y2 = guest2.coordinates[axisSelection.y - 1] || 0;
  const z2 = guest2.coordinates[axisSelection.z - 1] || 0;

  const points = useMemo(() => [
    [x1, y1, z1],
    [x2, y2, z2],
  ], [x1, y1, z1, x2, y2, z2]);

  return (
    <line>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={points.length}
          array={new Float32Array(points.flat())}
          itemSize={3}
        />
      </bufferGeometry>
      <lineBasicMaterial 
        color="#FF6B35" 
        linewidth={5}
        transparent
        opacity={0.9}
      />
    </line>
  );
}

const SceneContent = memo(function SceneContent() {
  const { 
    getGuestPoints, 
    getUserPoint,
    userCoordinates, // Need this for dependency tracking
    selectedGuest, 
    hoveredGuest,
    comparisonGuest,
    showComparisonLine,
    getGuestByName,
    getGuestCluster,
    setSelectedGuest,
    setHoveredGuest,
    comparisonMode,
    comparisonFirstGuest,
    setComparisonMode,
    setComparisonFirstGuest,
    selectedCluster,
    axisSelection,
    slices,
    cameraPosition,
    cameraTarget,
    resetTrigger,
    sizeMode,
    colorMode
  } = usePCAStore();
  
  // Auto-rotation state - starts true, stops on user interaction
  const [autoRotate, setAutoRotate] = useState(true);
  const hasUserInteracted = useRef(false);
  
  // Camera position for proximity labels
  const [cameraWorldPos, setCameraWorldPos] = useState<THREE.Vector3>(new THREE.Vector3(5, 5, 5));
  
  // Create a stable key for slices to use as dependency
  const slicesKey = useMemo(() => 
    slices.map(s => `${s.id}:${s.dimension}:${s.position}:${s.width}:${s.enabled}`).join('|'),
    [slices]
  );
  
  const guestPoints = useMemo(() => {
    return getGuestPoints();
  }, [getGuestPoints, axisSelection.x, axisSelection.y, axisSelection.z, slicesKey, sizeMode, colorMode]);

  const userPoint = useMemo(() => {
    return getUserPoint();
  }, [getUserPoint, userCoordinates, axisSelection.x, axisSelection.y, axisSelection.z]);
  const controlsRef = useRef<any>(null);
  
  // Track camera position each frame for proximity labels
  useFrame(({ camera }) => {
    setCameraWorldPos(camera.position.clone());
  });
  
  // Stop auto-rotation on user interaction
  const handleUserInteraction = () => {
    if (!hasUserInteracted.current) {
      hasUserInteracted.current = true;
      setAutoRotate(false);
    }
  };
  const { camera } = useThree();
  
  // Direct camera reset when resetTrigger changes
  const lastResetTriggerRef = useRef(0);
  useEffect(() => {
    if (controlsRef.current && resetTrigger > lastResetTriggerRef.current) {
      lastResetTriggerRef.current = resetTrigger;
      
      // Force immediate reset with animation
      const startPos = camera.position.clone();
      const startTarget = controlsRef.current.target?.clone() || new THREE.Vector3(0, 0, 0);
      const targetPos = new THREE.Vector3(...cameraPosition);
      const targetTarget = new THREE.Vector3(...cameraTarget);
      
      let progress = 0;
      const duration = 500;
      const startTime = Date.now();
      
      const animate = () => {
        const elapsed = Date.now() - startTime;
        progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        
        camera.position.lerpVectors(startPos, targetPos, eased);
        if (controlsRef.current?.target) {
          controlsRef.current.target.lerpVectors(startTarget, targetTarget, eased);
          controlsRef.current.update();
        }
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      
      requestAnimationFrame(animate);
    }
  }, [resetTrigger, cameraPosition, cameraTarget, camera]);
  
  // Get guests for connection line - handle __USER__ case
  const guest1 = selectedGuest ? getGuestByName(selectedGuest) : null;
  // For user comparison, create a fake guest object with userCoordinates
  const guest2 = comparisonGuest 
    ? (comparisonGuest === '__USER__' && userCoordinates 
        ? { coordinates: userCoordinates } 
        : getGuestByName(comparisonGuest))
    : null;

  return (
    <>
      {/* Lighting */}
      <ambientLight intensity={0.4} />
      <directionalLight position={[10, 10, 5]} intensity={0.8} />
      <pointLight position={[-10, -10, -5]} intensity={0.3} />
      
      {/* Grid - warm theme colors */}
      <Grid
        args={[20, 20]}
        cellColor="#FFE5D4"
        sectionColor="#FF8C42"
        cellThickness={0.5}
        sectionThickness={1}
        fadeDistance={25}
        fadeStrength={1}
      />
      
      {/* Axis helpers */}
      <axesHelper args={[5]} />
      
      {/* Connection line between selected and comparison guest - only when "Find Most Different" was clicked */}
      {showComparisonLine && guest1 && guest2 && (
        <ConnectionLine 
          guest1={guest1} 
          guest2={guest2} 
          axisSelection={axisSelection}
        />
      )}

      {/* Guest points */}
      {guestPoints.map((point, index) => {
        const pointCluster = getGuestCluster(point.name);
        return (
          <GuestPoint
            key={`${point.name}-${index}`}
            {...point}
            isHovered={hoveredGuest === point.name}
            isSelected={selectedGuest === point.name}
            isComparison={comparisonGuest === point.name}
            hasSelection={selectedGuest !== null}
            isInSelectedCluster={selectedCluster !== null && pointCluster === selectedCluster}
            hasClusterSelection={selectedCluster !== null}
            cameraPosition={cameraWorldPos}
            onHover={(hovered) => setHoveredGuest(hovered ? point.name : null)}
            onClick={() => {
            // Handle comparison mode clicks
            if (comparisonMode === 'waiting-first') {
              setComparisonFirstGuest(point.name);
              setSelectedGuest(point.name);
              setComparisonMode('waiting-second');
            } else if (comparisonMode === 'waiting-second' && comparisonFirstGuest) {
              // Use setCompareComparison to properly set up the comparison state
              const { setCompareComparison } = usePCAStore.getState();
              setCompareComparison(comparisonFirstGuest, point.name);
            } else {
              // Normal selection - clear any comparison state
              if (selectedGuest !== point.name) {
                setSelectedGuest(point.name);
              } else {
                setSelectedGuest(null);
              }
            }
          }}
          />
        );
      })}

      {/* User's Point - Special red, glowing, pulsating */}
      {userPoint && (
        <GuestPoint
          key="__USER__"
          {...userPoint}
          isHovered={hoveredGuest === '__USER__'}
          isSelected={selectedGuest === '__USER__'}
          isComparison={false}
          hasSelection={selectedGuest !== null}
          isInSelectedCluster={false}
          hasClusterSelection={selectedCluster !== null}
          cameraPosition={cameraWorldPos}
          onHover={(hovered) => setHoveredGuest(hovered ? '__USER__' : null)}
          onClick={() => {
            if (selectedGuest !== '__USER__') {
              setSelectedGuest('__USER__');
            } else {
              setSelectedGuest(null);
            }
          }}
          isUserPoint={true}
        />
      )}
      
      {/* Camera Focus Controller */}
      <CameraFocusController controlsRef={controlsRef} />
      
      {/* Controls */}
      <OrbitControls
        ref={controlsRef}
        enablePan={true}
        enableZoom={true}
        enableRotate={true}
        minDistance={2}
        maxDistance={35}
        autoRotate={autoRotate}
        autoRotateSpeed={0.3} // Very gentle rotation
        onStart={handleUserInteraction} // Stop auto-rotate when user starts interacting
      />
    </>
  );
});

export function Scene3D() {
  const { setSelectedGuest, selectedGuest } = usePCAStore();
  
  return (
    <div className="w-full h-full">
      <Canvas
        gl={{ antialias: true, alpha: true }}
        dpr={[1, 2]}
        className="bg-gradient-to-br from-warm-bg via-warm-surface to-warm-bg"
        onPointerMissed={(e) => {
          // Deselect when clicking on empty space (not on any object)
          if (e.type === 'click' && selectedGuest) {
            setSelectedGuest(null);
          }
        }}
      >
        <Suspense fallback={null}>
          <PerspectiveCamera makeDefault position={[5, 5, 5]} fov={50} />
          <Environment preset="night" />
          <SceneContent />
        </Suspense>
      </Canvas>
    </div>
  );
}
